// สถานะร้านแบบสด บน Firestore stores/{storeId} ให้ทีม support เปิดดูได้ทันที
// (เวอร์ชันแอป, jobchain ยาวแค่ไหน, ออเดอร์มาจากเครื่อง/มือถือไหนบ้าง, โต๊ะเปิด, ยอดค้าง/ยอดจ่าย)
//
// ไฟล์นี้คำนวณล้วน ๆ ห้าม import firebase / store / auto-import ของ nuxt
// ทดสอบได้ด้วย node ตรง ๆ: npm run test:store-status
// ตัวเขียนขึ้น Firestore อยู่ที่ modules/pos/composables/useStoreStatus.ts
//
// รูปร่าง doc (setDoc แบบ mergeFields — field อื่นของร้าน เช่น taxInfo / reload ไม่ถูกแตะ):
//   name, version, status: 'opened' | 'closed', opened (ISO), closed (ISO), crossDay,
//   live: {
//     analytic_add_counts: { cashier, cashier_2nd, terminal, mobile, qrcode, other },
//     deviceIds: { <ช่องทาง>_<id เครื่อง>(<ชื่อเครื่อง>): <จำนวน ADD job> }, ดู deviceKeyOfJob
//       เช่น main_cashier_ab12cd(แคชเชียร์หน้าร้าน), terminal_x9y8z7, mobile_flutter-AAAA(iPad ครัว), qr_code
//     bills, voided, paid, pending, tables, guests,
//     jobschain, businessDate, lastUpdated, writerComputerId, writerComputerName,
//   }

export type AddChannelBucket = "cashier" | "cashier_2nd" | "terminal" | "mobile" | "qrcode" | "other";

export const ADD_CHANNEL_BUCKETS: AddChannelBucket[] = [
  "cashier",
  "cashier_2nd",
  "terminal",
  "mobile",
  "qrcode",
  "other",
];

export interface StoreStatusLive {
  analytic_add_counts: Record<AddChannelBucket, number>;
  deviceIds: Record<string, number>;
  bills: number;
  voided: number;
  paid: number;
  pending: number;
  tables: number;
  guests: number;
  jobschain: number;
  businessDate: string | null;
  lastUpdated: string | null;
  writerComputerId: string | null;
  writerComputerName: string | null;
}

export interface StoreStatusDoc {
  name?: string;
  version: string;
  status?: "opened";
  opened?: string;
  crossDay?: boolean;
  live: StoreStatusLive;
}

export interface StoreStatusInput {
  name?: string | null;
  version: string;
  jobs: any[];
  tables?: Record<string, any> | null; // jobsStore.tables
  tablesV2?: Record<string, any> | null; // jobsStore.tables_v2
  tableState?: Record<string, any> | null; // posStore.tableState
  receipts?: any[] | null; // receiptsStore.receipts
  openShop?: any; // OPEN job ของรอบนี้
  closeJob?: any; // CLOSE job (มีแล้ว = กำลังปิดร้าน ห้ามเขียน status opened)
  crossDay?: boolean;
  writerComputerId?: string | null;
  writerComputerName?: string | null;
}

// key ของ map บน Firestore ห้ามว่าง และห้ามเป็นรูป __xxx__ (สงวนไว้)
// ตัด . # $ / [ ] ทิ้งด้วย เผื่อเอาไปใช้เป็น path ของ RTDB / field path
export const safeKey = (value: unknown): string => {
  const cleaned = String(value ?? "").replace(/[.#$/\[\]]/g, "_").trim();
  if (!cleaned) return "unknown";
  return /^__.*__$/.test(cleaned) ? `id_${cleaned}` : cleaned;
};

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

const toEpoch = (ts: unknown): number => {
  if (ts == null || ts === "") return NaN;
  return new Date(ts as any).getTime();
};

const toIso = (ts: unknown): string | null => {
  const t = toEpoch(ts);
  return isNaN(t) ? null : new Date(t).toISOString();
};

/**
 * ADD job นี้มาจากช่องทางไหน
 * POS ใส่ orderChannel = posStore.runmode (cart.ts) / มือถือ Flutter และ QR ใส่ channel หรือ orderChannel
 */
export function addChannelBucket(job: any): AddChannelBucket {
  const channel = String(job?.orderChannel || job?.channel || "").toLowerCase();
  switch (channel) {
    case "main_cashier":
      return "cashier";
    case "cashier_2nd":
      return "cashier_2nd";
    case "terminal_cashier":
    case "terminal_order":
      return "terminal";
    case "employee_mobile":
    case "mobile":
      return "mobile";
    case "qrcode":
      return "qrcode";
    default:
      return "other";
  }
}

// prefix ของ key ใน live.deviceIds (ตัวเล็กหมด) บอกว่าเครื่องนี้เป็นช่องทางไหน — ใช้นับเครื่องคิดราคา
// id และชื่อเครื่องที่ต่อท้ายคงตัวพิมพ์เดิม
export const DEVICE_KEY_PREFIX: Record<AddChannelBucket, string> = {
  cashier: "main_cashier",
  cashier_2nd: "cashier_2nd",
  terminal: "terminal",
  mobile: "mobile",
  qrcode: "qr_code",
  other: "other",
};

/**
 * id เครื่องที่ส่ง ADD job นี้ (ทั้งตัว ไม่ตัด)
 * POS ใส่ computerId (computerUuid) ให้ ADD ทุกใบ / job จากภายนอกลอง deviceId ก่อน
 */
export function deviceIdOfJob(job: any): string {
  const raw = job?.deviceId || job?.computerId || job?.computerUuid;
  return raw == null ? "" : String(raw).trim();
}

/**
 * ชื่อเครื่อง (ถ้ามี): POS = computerName (computerRemark_{storeId}) / มือถือ = deviceName
 * ไม่ใช้ชื่อพนักงาน — พนักงานสลับเครื่องกันได้ ชื่อในวงเล็บต้องเป็นของเครื่อง
 */
export function deviceNameOfJob(job: any): string {
  const raw = job?.computerName || job?.deviceName;
  return raw == null ? "" : String(raw).trim();
}

/**
 * key ของเครื่องที่ส่ง ADD job นี้ (ยังไม่ต่อชื่อเครื่อง) = {prefix ช่องทาง}_{id เครื่อง}
 * QR รวมเป็น key เดียว "qr_code" — ลูกค้าสั่งเอง ไม่รู้ว่าเครื่องไหน
 * ไม่มี id เลย (job รุ่นเก่า / มือถือที่ไม่ส่ง id) = "{prefix}_unknown"
 */
export function deviceKeyOfJob(job: any): string {
  const bucket = addChannelBucket(job);
  const prefix = DEVICE_KEY_PREFIX[bucket];
  if (bucket === "qrcode") return prefix;
  return safeKey(`${prefix}_${deviceIdOfJob(job) || "unknown"}`);
}

export function emptyAddCounts(): Record<AddChannelBucket, number> {
  return { cashier: 0, cashier_2nd: 0, terminal: 0, mobile: 0, qrcode: 0, other: 0 };
}

/** นับ ADD job แยกตามช่องทาง และแยกตามเครื่อง */
export function computeAddAnalytics(jobs: any[]) {
  const analytic_add_counts = emptyAddCounts();
  const counts: Record<string, number> = {};
  const names: Record<string, string> = {};
  for (const job of jobs || []) {
    if (job?.action !== "ADD") continue;
    const bucket = addChannelBucket(job);
    analytic_add_counts[bucket] += 1;
    const key = deviceKeyOfJob(job);
    counts[key] = (counts[key] || 0) + 1;
    // ชื่อล่าสุดของเครื่องนั้น — เปลี่ยนชื่อกลางวันก็ยังนับเป็นเครื่องเดียว
    const name = bucket === "qrcode" ? "" : deviceNameOfJob(job);
    if (name) names[key] = name;
  }
  // ต่อชื่อเครื่องในวงเล็บ: main_cashier_ab12cd(แคชเชียร์หน้าร้าน)
  const deviceIds: Record<string, number> = {};
  for (const [key, count] of Object.entries(counts)) {
    deviceIds[names[key] ? safeKey(`${key}(${names[key]})`) : key] = count;
  }
  return { analytic_add_counts, deviceIds };
}

const sumPayLines = (job: any) => {
  const lines = Array.isArray(job?.payment) ? job.payment : [];
  return lines.reduce((acc: number, line: any) => acc + (Number(line?.amount) || 0), 0);
};

/**
 * บิลของรอบนี้ = PAY job ใน jobchain (ครบทุกเครื่อง) จับคู่ใบเสร็จด้วย hash
 *
 * - มีใบเสร็จในเครื่อง: voided → นับเป็น voided / ไม่ voided → ใช้ payment.grandTotal
 * - ไม่มีใบเสร็จในเครื่อง (จ่ายจากเครื่องอื่นและยังไม่ได้ fetch): ใช้ยอดใน PAY job แทน
 *   (ยอดแต่ละช่องทางถูกตัดไม่ให้เกินยอดบิลอยู่แล้ว ดู _computePaymentTransformed ใน payment.ts)
 * ใบเสร็จที่ไม่มี PAY job คู่ในรอบนี้ (รอบก่อนหน้าในวันเดียวกัน) ไม่นับ
 */
export function computeReceiptTotals(jobs: any[], receipts: any[] | null | undefined) {
  const byHash = new Map<string, any>();
  for (const receipt of receipts || []) {
    const hash = receipt?.hash;
    if (!hash) continue;
    const prev = byHash.get(hash);
    // ใบเดียวกันอาจมีซ้ำ (add ตอนจ่าย + fetch) ถ้าใบไหนบอกว่า voided ให้ยึดอันนั้น
    if (!prev || (!prev.voided && receipt.voided)) byHash.set(hash, receipt);
  }

  let bills = 0;
  let voided = 0;
  let paid = 0;
  const seen = new Set<string>();
  for (const job of jobs || []) {
    if (job?.action !== "PAY") continue;
    const hash = job?.hash;
    if (hash) {
      if (seen.has(hash)) continue;
      seen.add(hash);
    }
    const receipt = hash ? byHash.get(hash) : null;
    if (receipt?.voided) {
      voided += 1;
      continue;
    }
    bills += 1;
    const total = Number(receipt?.payment?.grandTotal);
    paid += receipt && !isNaN(total) ? total : sumPayLines(job);
  }
  return { bills, voided, paid: round2(paid) };
}

/** จำนวนแขกของโต๊ะ: ค่าที่ตั้งไว้ใน tableState ก่อน แล้วค่อยค่าจาก jobchain (ADD ที่ไม่ได้ถามจำนวนแขกส่ง -1 มา) */
export function tableGuests(table: any, state: any): number {
  for (const value of [state?.guests, table?.guests]) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return 0;
}

/** โต๊ะที่เปิดอยู่ จำนวนแขก และยอดค้างจ่าย (grandTotal หลัง sc/tax/rounding จาก tables_v2) */
export function computeOpenTables(
  tables: Record<string, any> | null | undefined,
  tablesV2: Record<string, any> | null | undefined,
  tableState: Record<string, any> | null | undefined
) {
  let count = 0;
  let guests = 0;
  let pending = 0;
  for (const key of Object.keys(tables || {})) {
    count += 1;
    guests += tableGuests(tables?.[key], tableState?.[key]);
    pending += Number(tablesV2?.[key]?.grandTotal) || 0;
  }
  return { tables: count, guests, pending: round2(pending) };
}

/** เวลาของ job ล่าสุด (ISO) — job.timestamp มีทั้ง ISO string และตัวเลข */
export function maxJobTimestampIso(jobs: any[]): string | null {
  let max = NaN;
  for (const job of jobs || []) {
    const t = toEpoch(job?.timestamp);
    if (!isNaN(t) && (isNaN(max) || t > max)) max = t;
  }
  return isNaN(max) ? null : new Date(max).toISOString();
}

export function buildStoreStatusLive(input: StoreStatusInput): StoreStatusLive {
  const jobs = input.jobs || [];
  const { analytic_add_counts, deviceIds } = computeAddAnalytics(jobs);
  const { bills, voided, paid } = computeReceiptTotals(jobs, input.receipts);
  const { tables, guests, pending } = computeOpenTables(input.tables, input.tablesV2, input.tableState);
  return {
    analytic_add_counts,
    deviceIds,
    bills,
    voided,
    paid,
    pending,
    tables,
    guests,
    jobschain: jobs.length,
    businessDate: input.openShop?.businessDate || null,
    lastUpdated: maxJobTimestampIso(jobs),
    writerComputerId: input.writerComputerId || null,
    writerComputerName: input.writerComputerName || null,
  };
}

/**
 * doc ที่ main cashier เขียนทุกครั้งที่ jobchain เปลี่ยน
 * status/opened/crossDay ใส่เฉพาะตอนมี OPEN และยังไม่มี CLOSE
 * (ไม่งั้นจังหวะปิดร้านจะเขียน opened กลับไปทับ closed)
 */
export function buildStoreStatusDoc(input: StoreStatusInput): StoreStatusDoc {
  const doc: StoreStatusDoc = {
    version: input.version,
    live: buildStoreStatusLive(input),
  };
  const name = (input.name || "").trim();
  if (name) doc.name = name;

  if (input.openShop && !input.closeJob) {
    doc.status = "opened";
    const opened = toIso(input.openShop.timestamp);
    if (opened) doc.opened = opened;
    doc.crossDay = !!input.crossDay;
  }
  return doc;
}

/** live ตอนเปิด/ปิดร้าน: ตัวนับของรอบเป็นศูนย์ทั้งหมด */
export function emptyStoreStatusLive(
  businessDate: string | null,
  writer: { writerComputerId?: string | null; writerComputerName?: string | null } = {},
  lastUpdated: string | null = null
): StoreStatusLive {
  return {
    analytic_add_counts: emptyAddCounts(),
    deviceIds: {},
    bills: 0,
    voided: 0,
    paid: 0,
    pending: 0,
    tables: 0,
    guests: 0,
    jobschain: 0,
    businessDate: businessDate || null,
    lastUpdated,
    writerComputerId: writer.writerComputerId || null,
    writerComputerName: writer.writerComputerName || null,
  };
}

/**
 * field path สำหรับ setDoc(..., { mergeFields })
 *
 * ทำไมไม่ใช้ { merge: true }: merge จะ deep-merge map ทำให้ key ใน live.deviceIds ของเครื่องที่
 * หายไปแล้ว (หรือของรอบก่อน) ค้างอยู่ตลอด (Niceloop เจอปัญหานี้ เพราะจำ key เก่าไว้แค่ในหน่วยความจำ
 * reload แล้วลืม) mergeFields แทนที่ทั้งก้อนเฉพาะ path ที่ระบุ ส่วน field อื่นของ doc ไม่ถูกแตะ
 * จึงไม่ต้องจำ key เก่าหรืออ่าน doc ก่อนเขียน
 *
 * live แตกเป็นราย field (live.deviceIds, live.paid, ...) ไม่ใช่ "live" ทั้งก้อน
 * เผื่อมีระบบอื่นมาเขียน live.* เพิ่มในอนาคตจะได้ไม่ถูกลบทิ้ง
 */
export function storeStatusMergeFields(doc: Record<string, any>): string[] {
  const fields: string[] = [];
  for (const key of Object.keys(doc)) {
    if (key === "live" && doc.live && typeof doc.live === "object") {
      for (const liveKey of Object.keys(doc.live)) fields.push(`live.${liveKey}`);
    } else {
      fields.push(key);
    }
  }
  return fields;
}
